import { LazyQueryExecFunction, OperationVariables } from '@apollo/client'
import {
  Button,
  Chip,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme
} from '@material-ui/core'
import PeopleIcon from '@material-ui/icons/People'
import { useState } from 'react'
import Lightbox from 'yet-another-react-lightbox'
import Thumbnails from 'yet-another-react-lightbox/plugins/thumbnails'
import 'yet-another-react-lightbox/plugins/thumbnails.css'
import 'yet-another-react-lightbox/styles.css'

import NoPictureDeafault from '../../../../assets/images/NoPictureDeafault.png'
import insertPhoto from '../../../../assets/images/insertPhoto.svg'
import { getTranslate } from '../../../../localization'
import { CartResponse, ItemsInCart } from '../../../../types/cart'
import { Currency } from '../../../../types/currency'
import {
  RatePlanForSearch,
  RoomForSearch,
  SearchAvailabilityInput
} from '../../../../types/searchAvailability'
import { CreditorListItem } from '../../../../types/walletCreditor'
import SimpleFacility from '../Facility'
import RatePlanRow from '../RatePlanRow'
import useStyles from './styles/index.styles'

type PaymentTermsBalance = {
  walletName: string
  walletID: string
  creditorID?: string
  /** Undefined when the wallet funds this rate plan but which creditor does is unknowable here. */
  cashBalance?: number
  remainigCreditBalance?: number
}
/**
 * Which wallets can fund this rate plan, and what they have available.
 *
 * This used to compare `creditor.entity.displayEntityCode` against
 * `rp.displayProviderCode`. Those are two different code spaces: a channel whose
 * ShowProviderName is false is never told a provider identity, and search returns the
 * RATE GROUP code as the public handle. The comparison could not match, so the tooltip
 * showed a fabricated zero balance for wallets that had funds -- and once search began
 * returning a real verdict, it showed that zero NEXT TO a rate plan correctly marked
 * bookable, which reads as a contradiction rather than as the same underlying bug.
 *
 * Eligibility now comes from search per-wallet verdict, for the same reason the rate
 * plan own status does: it is the only component holding both the true provider code
 * and the channel flag.
 *
 * The BALANCE is a different question, and one this client cannot fully answer. A
 * bilateral wallet credit is extended per creditor, and the creditor funding this rate
 * plan is the one for its provider -- the identity being withheld. Where the wallet has
 * a single active creditor there is nothing to disambiguate and the figure is exact.
 * Where it has several, the amount is left UNDEFINED and rendered as unknown rather than
 * guessed: a plausible wrong number is worse than a visible absence here, because the
 * operator uses it to decide whether a booking will go through.
 *
 * Closing that gap properly means search returning the balance alongside the verdict.
 */
export const balancesForRatePlan = (
  rp: RatePlanForSearch,
  creditors: {
    walletName: string
    walletID: string
    creditorItems: CreditorListItem[]
  }[]
): PaymentTermsBalance[] => {
  // Absent verdicts mean search judged no wallet -- not that none qualify. Fall back to
  // the legacy match, which is still correct for a channel that shows provider names and
  // simply yields nothing for one that does not. Same degradation as the rate plan own
  // status: an unjudged response keeps what it had rather than reporting a refusal.
  if (!rp.walletEligibilities) {
    return creditors.flatMap((group) =>
      group.creditorItems
        .filter(
          (item) => item.entity.displayEntityCode === rp.displayProviderCode
        )
        .map((item) => ({
          walletName: group.walletName,
          walletID: group.walletID,
          creditorID: item.creditorID,
          cashBalance: item.walletBalance,
          remainigCreditBalance: item.remainingCreditBalance
        }))
    )
  }

  const fundable = new Set(
    rp.walletEligibilities
      .filter((verdict) => verdict.bookabilityStatus === 'BOOKABLE')
      .map((verdict) => verdict.walletID)
  )

  return creditors
    .filter((group) => fundable.has(group.walletID))
    .map((group) => {
      const active = group.creditorItems.filter((item) => item.active)
      const unambiguous = active.length === 1 ? active[0] : undefined

      return {
        walletName: group.walletName,
        walletID: group.walletID,
        creditorID: unambiguous?.creditorID,
        cashBalance: unambiguous?.walletBalance,
        remainigCreditBalance: unambiguous?.remainingCreditBalance
      }
    })
}

type Props = {
  room: RoomForSearch
  nonBillateralWallet:
    | {
        walletName: string
        walletID: string
        creditorID?: undefined
        cashBalance: number | 0
        remainigCreditBalance: number | 0
      }
    | undefined

  creditors: {
    walletName: string
    walletID: string
    creditorItems: CreditorListItem[]
  }[]
  currency: Currency
  validProviderCode: string
  setValidProviderCode: React.Dispatch<React.SetStateAction<string>>
  searchAvailabilityInput: SearchAvailabilityInput
  selectedWalletType: {
    walletName: string
    walletID: string
  }
  getCart: LazyQueryExecFunction<
    {
      cartResponse: CartResponse
    },
    OperationVariables
  >
  loadingForGetCart: boolean
  setSelectedItemHash: React.Dispatch<React.SetStateAction<string>>
  selectedItemHash: string
  cartData:
    | {
        cartResponse: CartResponse
      }
    | undefined

  uniqueProviders: {
    title: string
    value: string
    checked: boolean
  }[]
  itemsInCart: ItemsInCart
  setItemsInCart: React.Dispatch<React.SetStateAction<ItemsInCart>>
}

const RoomTypeDetails = ({
  room,
  nonBillateralWallet,
  creditors,
  currency,
  validProviderCode,
  setValidProviderCode,
  searchAvailabilityInput,
  selectedWalletType,
  getCart,
  loadingForGetCart,
  selectedItemHash,
  setSelectedItemHash,
  cartData,
  uniqueProviders,
  itemsInCart,
  setItemsInCart
}: Props) => {
  const classes = useStyles()
  const {
    roomTypeName,
    roomTypeCode,
    displayRoomTypeCode,
    images,
    ratePlans,
    occupancy,
    facilities
  } = room
  const MAX_VISIBLE = 10
  const visibleFacilities = facilities.slice(0, MAX_VISIBLE)
  const hiddenFacilities = facilities.slice(MAX_VISIBLE)
  const [showAllFacility, setShowAllFacility] = useState(false)
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const [open, setOpen] = useState(false)
  const slides = images.map((img) => ({
    src: img
  }))

  return (
    <>
      <div className={classes.root}>
        <div className={classes.header}>
          <div
            className={[
              classes.imageContainer,
              slides.length > 1 && classes.imageContainerPointer
            ].join(' ')}>
            <img
              src={images.length > 0 ? images[0] : NoPictureDeafault}
              className={classes.image}
            />
            {slides.length > 1 && (
              <div className={classes.counter} onClick={() => setOpen(true)}>
                <img src={insertPhoto} className={classes.photoIcon} />
                <Typography variant="h5" className={classes.iconText}>
                  {` +${slides.length - 1}`}
                </Typography>
              </div>
            )}
          </div>
          <div className={classes.details}>
            <Typography variant="subtitle1" className={classes.sub}>
              {`${roomTypeName} (${displayRoomTypeCode})`}
            </Typography>
            <div className={classes.individual}>
              <div className={classes.guest}>
                <PeopleIcon className={classes.icon} />
                <Typography
                  variant={isMobile ? 'h6' : 'h5'}
                  className={classes.headerText}>
                  {`${occupancy.adults} ${getTranslate('Adults')}`}
                  {occupancy.extraBeds > 0
                    ? ` | ${occupancy.extraBeds} ${getTranslate('Extra Bed')}`
                    : ''}
                </Typography>
              </div>
              <Chip
                label={
                  <Typography variant="h5" className={classes.headerText}>
                    {`${getTranslate('Max')} : ${
                      occupancy.total
                    } ${getTranslate('Adults')}`}
                  </Typography>
                }
                size="small"
              />
            </div>
            {!isMobile && facilities && facilities.length > 0 && (
              <>
                <Typography variant="subtitle2" className={classes.sub}>
                  {getTranslate('Facilities')}
                </Typography>

                <div className={classes.facilityContainer}>
                  {visibleFacilities.map((fac, i) => (
                    <SimpleFacility title={fac.Name} key={i} />
                  ))}

                  {hiddenFacilities.length > 0 && (
                    <Tooltip
                      placement="top"
                      title={
                        <div>
                          {hiddenFacilities.map((fac, i) => (
                            <Typography variant="body2" key={i}>
                              {getTranslate(fac.Name)}
                            </Typography>
                          ))}
                        </div>
                      }>
                      <div className={classes.showMore}>
                        <Typography variant="h5" style={{ fontWeight: 600 }}>
                          {`+${hiddenFacilities.length} ${getTranslate(
                            'more'
                          )}`}
                        </Typography>
                      </div>
                    </Tooltip>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
        <div className={classes.content}>
          {!isMobile ? (
            <div className={classes.tableHeader}>
              <Typography className={classes.sub}>
                {getTranslate('Rate Plans')}
              </Typography>
              <Typography
                className={[classes.sub, classes.middleText].join(' ')}>
                {getTranslate(`Provider`)}
              </Typography>
              <Typography
                className={[classes.sub, classes.middleText].join(' ')}>
                {getTranslate(`Today's Price`)}
              </Typography>
            </div>
          ) : (
            facilities.length > 0 && (
              <>
                <div>
                  <Button
                    variant="text"
                    size="small"
                    className={classes.btn}
                    onClick={() => setShowAllFacility(!showAllFacility)}>
                    <Typography variant="h5" className={classes.facilityText}>
                      {showAllFacility
                        ? getTranslate('Show Less Facilities')
                        : getTranslate('Show All Facilities')}
                    </Typography>
                  </Button>

                  {showAllFacility && (
                    <div className={classes.facilityContainer}>
                      {facilities.map((fac, i) => (
                        <SimpleFacility title={fac.Name} key={i} />
                      ))}
                    </div>
                  )}
                </div>
                <Typography variant="h4" className={classes.headerforRateplans}>
                  {getTranslate('Rate Plans')}
                </Typography>
              </>
            )
          )}

          {ratePlans.map((rp, index) => {
            const paymentTermsBalance = balancesForRatePlan(rp, creditors)
            rp.availablePaymentMethods.map((method) => {
              if (method === 'AdoPay' && nonBillateralWallet) {
                paymentTermsBalance.push(nonBillateralWallet)
              }
            })
            return (
              <RatePlanRow
                key={index}
                ratePlan={rp}
                paymentTermsBalance={paymentTermsBalance}
                currency={currency}
                disable={
                  rp.disable ||
                  (validProviderCode !== '' &&
                    validProviderCode !== rp.displayProviderCode)
                }
                changeProvider={
                  validProviderCode !== '' &&
                  validProviderCode !== rp.displayProviderCode
                }
                validProviderCode={validProviderCode}
                setValidProviderCode={setValidProviderCode}
                roomTypeCode={roomTypeCode}
                occupancy={occupancy}
                searchAvailabilityInput={searchAvailabilityInput}
                selectedWalletType={selectedWalletType}
                getCart={getCart}
                loadingForGetCart={loadingForGetCart}
                selectedItemHash={selectedItemHash}
                setSelectedItemHash={setSelectedItemHash}
                cartData={cartData?.cartResponse}
                uniqueProviders={uniqueProviders}
                itemsInCart={itemsInCart}
                setItemsInCart={setItemsInCart}
              />
            )
          })}
        </div>
      </div>
      <Lightbox
        open={open}
        close={() => setOpen(false)}
        slides={slides}
        thumbnails={{
          position: 'bottom',
          width: 100,
          height: 100,
          imageFit: 'cover'
        }}
        plugins={[Thumbnails]}
        carousel={{
          finite: true
        }}
        styles={{
          container: {
            backgroundColor: 'black'
          }
        }}
      />
    </>
  )
}

export default RoomTypeDetails
